import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, CalendarDays, Car, Bell, Inbox, Settings, LogOut, Menu, X, Plus, AlertOctagon, BellRing, Users, Crown, IdCard, Receipt, Wallet, Globe, MessageSquare, Star, Gift, ExternalLink, AlertTriangle, Activity, GraduationCap, Clock, ClipboardCheck, Headset, ClipboardList, PlayCircle, Contact, CalendarRange, BarChart3, LifeBuoy, BookOpenCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import WarningBanner from "./WarningBanner";
import LocationWarningsBanner from "./LocationWarningsBanner";
import NoIndex from "./NoIndex";
import { BrandLogo } from "./BrandLogo";
import ClockWidget from "./ClockWidget";
import NotificationBell from "./NotificationBell";
import BackToConfirmPHMenu, { BackToConfirmPHSidebar } from "./BackToConfirmPHMenu";
import { useT } from "@/i18n/I18nProvider";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

// nav.requires: permission group the employee must have. Owners see everything.
// nav.ownerOnly: only the owner can see the link, even if employee has the perm.
// nav.group: which sidebar section the item belongs to.
//
// Phase 2J navigation cleanup: collapsed 27 top-level items down to the
// screens staff actually visit daily/weekly. Low-frequency destinations
// (public profile, mini-site, website integrations, plans & billing,
// support, partner rewards, staff training) still exist at their same
// routes: they just moved one click deeper, under Settings, instead of
// competing for sidebar space (spec P/R). The exact duplicate
// /app/recruitment route was removed outright (kept only under /admin,
// where its single-hardcoded-email gate actually belongs).
const NAV = [
  // Daily: the actual workspace
  { to: "/app",               labelKey: "nav.dashboard",         icon: LayoutDashboard, end: true, testId: "nav-dashboard", requires: "bookings", group: "daily" },
  { to: "/app/bookings",      labelKey: "nav.bookings",          icon: CalendarDays,    testId: "nav-bookings", requires: "bookings",            group: "daily" },
  { to: "/app/fleet-calendar", labelKey: "nav.fleet_calendar",   icon: CalendarRange,   testId: "nav-fleet-calendar", requires: "bookings",       group: "daily" },
  { to: "/app/booking-intake", labelKey: "nav.booking_intake",   icon: ClipboardList,   testId: "nav-booking-intake", requires: "booking_intake", group: "daily" },
  { to: "/app/vehicles",      labelKey: "nav.vehicles",          icon: Car,             testId: "nav-vehicles", requires: "vehicles",            group: "daily" },
  { to: "/app/analytics/fleet", labelKey: "nav.fleet_performance", icon: BarChart3,     testId: "nav-fleet-performance", requires: "reports",     group: "daily" },
  { to: "/app/customers",     labelKey: "nav.customers",         icon: Contact,         testId: "nav-customers", requires: "bookings",           group: "daily" },
  { to: "/app/requests",      labelKey: "nav.customer_requests", icon: Inbox,           testId: "nav-requests", requires: "verification_requests", group: "daily" },
  { to: "/app/incidents",     labelKey: "nav.problems",          icon: AlertOctagon,    testId: "nav-incidents", requires: "incidents",          group: "daily" },
  { to: "/app/warnings",      labelKey: "nav.warnings",          icon: AlertTriangle,   testId: "nav-warnings",  requires: "bookings",           group: "daily" },
  { to: "/app/activity",      labelKey: "nav.activity",          icon: Activity,        testId: "nav-activity",  requires: "bookings",           group: "daily" },
  { to: "/app/inquiries",     labelKey: "nav.inquiries",         icon: MessageSquare,   testId: "nav-inquiries", requires: "bookings",           group: "daily" },
  { to: "/app/reminders",     labelKey: "nav.followups",         icon: BellRing,        testId: "nav-reminders", requires: "reminders",          group: "daily" },
  // Business: runs the business, weekly/monthly cadence
  { to: "/app/administration", labelKey: "nav.administration",  icon: Wallet,          testId: "nav-administration", requires: "finances",  group: "business" },
  // Finance & Books: visible to anyone holding a finance capability (the
  // legacy "finances" group maps to view/record/export, so existing finance
  // staff keep seeing it) - never widened beyond that.
  { to: "/app/finance",       label: "Finance & Books",          icon: BookOpenCheck,   testId: "nav-finance-books", requiresAny: ["finances", "finance.view", "finance.record_payment", "finance.record_expense", "finance.post", "finance.export", "finance.configure", "finance.deposits.refund", "finance.deposits.apply", "finance.correct", "finance.close_period"], group: "business" },
  // Legal Readiness: visible to anyone holding a compliance capability
  // (owners implicitly). Its own capabilities, never implied by "vehicles".
  { to: "/app/compliance",    label: "Compliance",               icon: ClipboardCheck,  testId: "nav-compliance", requiresAny: ["compliance.view", "compliance.manage", "compliance.documents.view", "compliance.documents.upload", "compliance.vehicle.view", "compliance.vehicle.manage", "compliance.insurance.view", "compliance.insurance.manage", "compliance.export", "compliance.tax.view"], group: "business" },
  { to: "/app/reviews",       labelKey: "nav.reviews",           icon: Star,            testId: "nav-reviews",                                  ownerOnly: true,           group: "business" },
  { to: "/app/checklists",    labelKey: "nav.daily_checklists",  icon: ClipboardCheck,  testId: "nav-checklists",                                         group: "business" },
  { to: "/app/notifications", labelKey: "nav.messages",          icon: Bell,            testId: "nav-notifications",                            requires: "reports",     group: "business" },
  { to: "/app/help",          labelKey: "nav.help",              icon: LifeBuoy,        testId: "nav-help",                                                                group: "business" },
  // Management: owner-run, low-frequency
  { to: "/app/employees",     labelKey: "nav.employees",         icon: Users,           testId: "nav-employees",                                ownerOnly: true,         group: "management" },
  { to: "/app/driver-service", label: "Driver service",          icon: Car,             testId: "nav-driver-service", requiresAny: ["drivers", "drivers.view", "drivers.manage", "drivers.assign", "drivers.availability.manage", "drivers.compliance.view", "drivers.compliance.manage", "drivers.documents.view", "drivers.safety.manage", "drivers.dispatch.acknowledge", "drivers.settings.manage", "drivers.reports.view"], requiresCompanySetting: "driver_services_enabled", group: "management" },
  { to: "/app/renter-drivers", label: "Self-drive drivers",      icon: IdCard,          testId: "nav-renter-drivers", requiresAny: ["bookings", "renter_drivers.view", "renter_drivers.manage", "renter_drivers.document_view", "renter_drivers.document_review", "renter_drivers.release_check", "renter_drivers.policy_configure", "renter_drivers.policy_override"], group: "management" },
  { to: "/app/rental-settlement", label: "Rental settlement", icon: IdCard,      testId: "nav-rental-settlement", requiresAny: ["bookings", "rental.view", "rental.damage_manage", "rental.charge_propose", "rental.charge_establish", "rental.deposit_apply", "rental.deposit_refund", "rental.dispute_manage", "rental.claim_manage", "rental.close", "rental.policy_configure"], group: "management" },
  { to: "/app/driver",        label: "My trips",                 icon: Car,             testId: "nav-driver-workspace", driverOnly: true, group: "daily" },
  { to: "/app/drivers",       labelKey: "nav.drivers",           icon: IdCard,          testId: "nav-drivers",                                  requires: "drivers",     requiresCompanySetting: "driver_services_enabled", group: "management" },
  { to: "/app/staff",         labelKey: "nav.staff",             icon: Clock,           testId: "nav-staff",                                                                  group: "management" },
  { to: "/app/staff-management", label: "Staff administration", icon: ClipboardList, testId: "nav-staff-management", requires: "employees", group: "management" },
  { to: "/app/settings",      labelKey: "nav.settings",          icon: Settings,        testId: "nav-settings",                                 requires: "settings",    group: "management" },
  { to: "/admin/recruitment", label: "Recruitment",              icon: Users,           testId: "nav-recruitment",                              recruitmentAdminOnly: true, group: "management" },
];

const GROUPS = [
  { key: "daily",      titleKey: "nav.group.daily" },
  { key: "business",   titleKey: "nav.group.business" },
  { key: "management", titleKey: "nav.group.manage" },
];

function hasAccess(user, item, company) {
  // Business-level feature toggles (e.g. driver services) gate visibility
  // for EVERYONE, owners included: a self-drive-only business shouldn't
  // see driver nav just because the logged-in account is the owner.
  if (item.requiresCompanySetting && !company?.[item.requiresCompanySetting]) return false;
  const role = user?.account_type || user?.role;
  const email = (user?.email || "").toLowerCase();
  const isRecruitmentAdmin =
    role === "platform_admin" || email === "andyvc00@hotmail.com";
  if (item.recruitmentAdminOnly) return isRecruitmentAdmin;
  // Task 5: the driver workspace is for people who are drivers, whatever their role.
  if (item.driverOnly) return !!user?.is_driver;
  if (role === "business_owner" || role === "platform_admin") return true;
  if (item.ownerOnly) return false;
  const permissions = user?.permissions || [];
  if (item.requiresAny) return item.requiresAny.some((p) => permissions.includes(p));
  if (!item.requires) return true;
  return permissions.includes(item.requires);
}

export default function DashboardLayout() {
  const { user, company, logout } = useAuth();  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));
  const [inquiriesUnread, setInquiriesUnread] = useState(0);

  const handleLogout = () => {
    // Warn if there are unsynced quick notes on this device.
    import("../lib/notepadStore").then(async ({ countUnsynced }) => {
      const companyId = company?.id || user?.company_id;
      const n = companyId ? await countUnsynced(companyId).catch(() => 0) : 0;
      if (n > 0 && !window.confirm(
        `You have ${n} unsynced staff note${n === 1 ? "" : "s"} on this device. ` +
        "Sync or discard them before logging out: otherwise the notes stay " +
        "on this device only and won't reach ConfirmPH.\n\nLog out anyway?"
      )) {
        return;
      }
      logout(); navigate("/");
    }).catch(() => { logout(); navigate("/"); });
  };
  const visibleNav = NAV.filter((item) => hasAccess(user, item, company));
  const canCreateBookings = hasAccess(user, { requires: "bookings" });
  const role = user?.account_type || user?.role;
  const isOwner = role === "business_owner" || role === "platform_admin";
  const isDemoCompany = !!company?.is_demo;

  // Poll the unread-inquiries count so the sidebar badge stays in sync
  // even after the owner marks an inquiry as responded/archived on the
  // Inquiries page.
  useEffect(() => {
    if (!user || !company) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/inquiries/unread-count");
        if (!cancelled) setInquiriesUnread(data?.count || 0);
      } catch (_e) { /* silent: badge is non-critical */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 45_000);
    return () => { cancelled = true; clearInterval(id); };
    // Re-fetch when route changes (e.g., owner just visited /app/inquiries)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, company?.id, location.pathname]);

  const badgeFor = (to) => {
    if (to === "/app/inquiries" && inquiriesUnread > 0) return inquiriesUnread;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900">
      <NoIndex title="Dashboard · ConfirmPH" />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center gap-3">
            <button data-testid="sidebar-toggle" className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
                    ref={triggerRef} aria-label="Open business navigation" aria-expanded={open} aria-controls="business-mobile-navigation"
                    onClick={() => setOpen(!open)}>
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <Link to="/app" className="flex items-center gap-2.5">
              <BrandLogo
                variant="full"
                className="h-9 w-auto"
                alt="ConfirmPH"
                testId="dashboard-brand-logo"
              />
              {company?.name ? (
                <div className="hidden sm:block leading-tight border-l border-slate-200 pl-2.5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Business</div>
                  <div className="text-sm font-medium text-slate-700 truncate max-w-[180px]">{company.name}</div>
                </div>
              ) : null}
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/demo-business/scenarios"
                  data-testid="dashboard-topbar-demo-scenarios"
                  className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-semibold hover:bg-emerald-100"
                  title="Explore interactive ConfirmPH scenarios">
              <PlayCircle className="w-3.5 h-3.5" /> Demo Scenarios
            </Link>
            {canCreateBookings && (
              <Link to="/app/bookings/new" data-testid="topbar-new-booking-btn"
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-brand text-sm font-medium">
                <Plus className="w-4 h-4" /> {t("btn.add_booking")}
              </Link>
            )}
            <ClockWidget />
            <NotificationBell />
            <LanguageSwitcher />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="role-badge">
                {isOwner ? <Crown className="w-3 h-3 text-amber-600" /> : <IdCard className="w-3 h-3 text-slate-500" />}
                {isOwner ? "Owner" : "Employee"} · {user?.email}
              </div>
            </div>
            <Link to="/" data-testid="biz-view-site"
                   className="hidden sm:hidden items-center gap-1 px-2 py-1.5 rounded-md hover:bg-slate-100 text-xs text-slate-600">
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <BackToConfirmPHMenu user={user} company={company} />
            <button data-testid="logout-btn" onClick={handleLogout}
                    className="p-2 rounded-md hover:bg-slate-100 text-slate-700" title={t("nav.logout")}>
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {open && <div aria-hidden="true" data-overlay-backdrop className="fixed inset-0 top-16 z-20 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
        <aside ref={panelRef} id="business-mobile-navigation" aria-label="Business navigation" tabIndex={open ? -1 : undefined}
               role={open ? "dialog" : undefined} aria-modal={open ? "true" : undefined}
               className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-16 lg:top-16 left-0 z-30 w-64 h-[calc(100vh-4rem)] bg-white border-r border-slate-200 overflow-y-auto`}>
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 lg:hidden">
            <span className="font-semibold">Business menu</span>
            <button type="button" data-autofocus aria-label="Close business navigation" onClick={() => setOpen(false)} className="grid min-h-11 min-w-11 place-items-center rounded-md hover:bg-slate-100"><X className="h-5 w-5" aria-hidden="true" /></button>
          </div>
          <nav className="p-3 space-y-4">
            <div data-testid="dashboard-sidebar-learn-group">
              <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {isDemoCompany ? "Demo business" : "Learn"}
              </div>
              <Link to="/demo-business/scenarios"
                     data-testid="dashboard-sidebar-demo-scenarios"
                     onClick={() => setOpen(false)}
                     className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 font-medium hover:bg-emerald-100">
                <PlayCircle className="w-4 h-4" strokeWidth={1.8} />
                <span className="flex-1">Demo Scenarios</span>
                <span className="text-[10px] text-emerald-700">Try it →</span>
              </Link>
              <p className="mt-1.5 mx-1 text-[10px] leading-snug text-slate-500">
                {isDemoCompany
                  ? "Explore the same ConfirmPH situations your customers see, using fake data only."
                  : "See how ConfirmPH handles common rental situations. Safe sandbox: nothing you do here touches your live business."}
              </p>
            </div>
            {GROUPS.map((g) => {
              const items = visibleNav.filter((i) => i.group === g.key);
              if (items.length === 0) return null;
              return (
                <div key={g.key}>
                  <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                    {t(g.titleKey)}
                  </div>
                  <div className="space-y-1">
                    {items.map(({ to, labelKey, label, icon: Icon, end, testId }) => {
                      const badge = badgeFor(to);
                      return (
                        <NavLink
                          key={to} to={to} end={end} data-testid={testId} onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                              isActive ? "bg-[#1B4332] text-white" : "text-slate-700 hover:bg-slate-100"
                            }`
                          }
                        >
                          <Icon className="w-4 h-4" strokeWidth={1.8} />
                          <span className="flex-1">{labelKey ? t(labelKey) : label}</span>
                          {badge ? (
                            <span
                              data-testid={`${testId}-badge`}
                              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-amber-500 text-white"
                            >
                              {badge > 99 ? "99+" : badge}
                            </span>
                          ) : null}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className="p-4 mt-2 border-t border-slate-200">
            {canCreateBookings && (
              <Link to="/app/bookings/new" data-testid="sidebar-new-booking-btn"
                    className="sm:hidden flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg btn-brand text-sm font-medium">
                <Plus className="w-4 h-4" /> {t("btn.add_booking")}
              </Link>
            )}
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              {isOwner
                ? "Bookings only become truly confirmed once your client reviews and confirms via their secure link."
                : `Logged in as employee. You see what your owner allowed.`}
            </p>
            <BackToConfirmPHSidebar user={user} company={company} />
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          {company?.is_demo && <DemoBanner />}
          <WarningBanner />
          <LocationWarningsBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function DemoBanner() {
  return (
    <div
      data-testid="demo-account-banner"
      className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-100 grid place-items-center flex-shrink-0">
        <svg className="w-4 h-4 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-emerald-900">You&apos;re exploring the demo: Dumaguete Island Wheels</div>
        <p className="text-xs text-emerald-900/80 mt-1 leading-relaxed">
          Click around, edit, add, or delete anything: it&apos;s a safe sandbox. Real emails and payments are mocked.
          Every time you log in, the demo resets to its canonical state, so feel free to experiment.
          <span className="block sm:inline mt-1 sm:mt-0 sm:ml-2">Ready to start your own?{" "}
            <Link to="/register?as=business" data-testid="demo-banner-register" className="underline font-medium hover:no-underline">
              Create a free 30-day trial →
            </Link>
          </span>
        </p>
      </div>
    </div>
  );
}
